import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Building2, MapPin, Phone, Users, TrendingUp, Calendar, MoreVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Account } from '@/types'
import { formatCurrency, formatDate } from '@/lib/utils'

interface MobileAccountCardProps {
  account: Account
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onEdit?: (account: Account) => void
  onDelete?: (account: Account) => void
}

export function MobileAccountCard({
  account,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileAccountCardProps) {
  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(account.id)}
              aria-label={`Select ${account.name}`}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Link
                to={`/accounts/${account.id}`}
                className="font-semibold text-base hover:underline focus:underline focus:outline-none"
              >
                {account.name}
              </Link>
              {account.type && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {account.type}
                </Badge>
              )}
              {account.rating && account.rating === 'Hot' && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Hot
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${account.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/accounts/${account.id}`}>View Details</Link>
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(account)}>Edit Account</DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(account)}
                  className="text-destructive focus:text-destructive"
                >
                  Delete Account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {account.industry && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{account.industry}</span>
          </div>
        )}

        {(account.billing_city || account.billing_state) && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {[account.billing_city, account.billing_state].filter(Boolean).join(', ')}
            </span>
          </div>
        )}

        {account.phone && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{account.phone}</span>
          </div>
        )}

        {account.owner_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{account.owner_name}</span>
          </div>
        )}

        {account.annual_revenue && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <TrendingUp className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">
              {formatCurrency(account.annual_revenue, account.currency_iso_code || 'CAD')}
            </span>
          </div>
        )}

        {account.created_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs">Created {formatDate(account.created_date)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileAccountListProps {
  accounts: Account[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onEdit?: (account: Account) => void
  onDelete?: (account: Account) => void
}

export function MobileAccountList({
  accounts,
  selectedIds,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileAccountListProps) {
  return (
    <div className="space-y-3">
      {accounts.map((account) => (
        <MobileAccountCard
          key={account.id}
          account={account}
          isSelected={selectedIds.has(account.id)}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
